import React, { useEffect, useMemo, useState } from 'react';
import { Upload, Lock, FileText, Settings, Archive, AlertTriangle, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useEditorConfig } from '../../../contexts/EditorContext';

interface FileImportTabProps {
    data: any;
    onChange: (key: string, value: any) => void;
}

export const FileImportTab: React.FC<FileImportTabProps> = ({ data, onChange }) => {
    const { services } = useEditorConfig();
    const parserType = data.fileImportParserType || 'Auto';
    const authType = data.fileImportAuthType || 'None';
    const [authExpanded, setAuthExpanded] = useState(authType !== 'None');
    const [csvExpanded, setCsvExpanded] = useState(parserType === 'Csv');
    const [jsonExpanded, setJsonExpanded] = useState(parserType === 'Json');
    const [xmlExpanded, setXmlExpanded] = useState(parserType === 'Xml');

    // Workflow File → Process → Start Event selectors
    const callableProcesses = services.callableProcesses ?? [];
    const [selectedFileId, setSelectedFileId] = useState<string>(data.fileImportWorkflowFileId || '');
    const workflows = useMemo(() => {
        const unique = new Map<string, { id: string; name: string; version?: number }>();
        for (const process of callableProcesses) {
            const id = process.workflowFileId || process.collection;
            if (!unique.has(id)) unique.set(id, { id, name: process.collection, version: process.version });
        }
        return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [callableProcesses]);
    const processes = useMemo(
        () => callableProcesses.filter(process => (process.workflowFileId || process.collection) === selectedFileId),
        [callableProcesses, selectedFileId],
    );

    // Derive the currently selected process index from data
    const selectedProcessName = data.fileImportProcessId || '';
    const selectedProcess = processes.find(p => p.processName === selectedProcessName);

    useEffect(() => {
        if (processes.length === 1 && !data.fileImportProcessId) {
            onChange('fileImportProcessId', processes[0].processName);
        }
    }, [data.fileImportProcessId, onChange, processes]);

    // When process changes and has only one start event, auto-select it
    useEffect(() => {
        if (selectedProcess?.startEvents.length === 1 && !data.fileImportStartEventId) {
            onChange('fileImportStartEventId', selectedProcess.startEvents[0].id);
        }
    }, [data.fileImportStartEventId, onChange, selectedProcess]);

    const handleFileChange = (fileId: string) => {
        setSelectedFileId(fileId);
        onChange('fileImportWorkflowFileId', fileId);
        onChange('fileImportProcessId', '');
        onChange('fileImportStartEventId', '');
        onChange('fileImportProcessVersion', '');
    };

    const handleProcessChange = (processName: string) => {
        onChange('fileImportProcessId', processName);
        onChange('fileImportStartEventId', '');
    };

    const columnNames: string[] = data.fileImportCsvColumnNames || [];

    const handleAddColumn = () => {
        onChange('fileImportCsvColumnNames', [...columnNames, `Column${columnNames.length}`]);
    };

    const handleRemoveColumn = (index: number) => {
        onChange('fileImportCsvColumnNames', columnNames.filter((_, i) => i !== index));
    };

    const handleColumnChange = (index: number, value: string) => {
        const updated = [...columnNames];
        updated[index] = value;
        onChange('fileImportCsvColumnNames', updated);
    };

    const inputClass = "w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white";
    const labelClass = "block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1";
    const sectionHeaderClass = "flex items-center gap-2 cursor-pointer select-none";

    return (
        <div className="p-6 space-y-6 overflow-y-auto">
            {/* Info Box */}
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-900 flex gap-3">
                <Upload className="text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-teal-800 dark:text-teal-300">File Import Task</p>
                    <p className="text-[11px] text-teal-700 dark:text-teal-400 leading-relaxed">
                        Reads files from local, SMB, or WebDAV sources, parses records, and starts a process instance for each record.
                    </p>
                </div>
            </div>

            {/* Source URI */}
            <div>
                <label className={labelClass}>Source File URI</label>
                <input
                    type="text"
                    value={data.fileImportSourceUri || ''}
                    onChange={e => onChange('fileImportSourceUri', e.target.value)}
                    placeholder="file://C:/data/import.csv or smb://server/share/data.json"
                    className={`${inputClass} font-mono`}
                />
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                    Supports file://, smb://, webdav://, and UNC paths. Use {'${variable}'} for dynamic values.
                </p>
            </div>

            {/* Parser Type */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Parser Type</label>
                    <select
                        value={parserType}
                        onChange={e => onChange('fileImportParserType', e.target.value)}
                        className={inputClass}
                    >
                        <option value="Auto">Auto-detect</option>
                        <option value="Csv">CSV</option>
                        <option value="Json">JSON</option>
                        <option value="Xml">XML</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Result Variable</label>
                    <input
                        type="text"
                        value={data.resultVariable || ''}
                        onChange={e => onChange('resultVariable', e.target.value)}
                        placeholder="importResult"
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Authentication (Collapsible) */}
            <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                <div className={sectionHeaderClass} onClick={() => setAuthExpanded(!authExpanded)}>
                    {authExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                    <Lock size={14} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Authentication</span>
                    {authType !== 'None' && <span className="text-[9px] bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 px-1.5 rounded-full font-bold">{authType}</span>}
                </div>
                {authExpanded && (
                    <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                        <div>
                            <label className={labelClass}>Auth Type</label>
                            <select
                                value={authType}
                                onChange={e => onChange('fileImportAuthType', e.target.value)}
                                className={inputClass}
                            >
                                <option value="None">None</option>
                                <option value="Basic">Basic Auth</option>
                                <option value="Bearer">Bearer Token</option>
                                <option value="ApiKey">API Key</option>
                                <option value="Windows">Windows/NTLM</option>
                            </select>
                        </div>
                        {authType === 'Basic' && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Username</label>
                                    <input type="text" value={data.fileImportAuthUsername || ''} onChange={e => onChange('fileImportAuthUsername', e.target.value)} placeholder="username" className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input type="password" value={data.fileImportAuthPassword || ''} onChange={e => onChange('fileImportAuthPassword', e.target.value)} placeholder="password or {{env.VAR}}" className={inputClass} />
                                </div>
                                <div className="col-span-2">
                                    <label className={labelClass}>Domain</label>
                                    <input type="text" value={data.fileImportAuthDomain || ''} onChange={e => onChange('fileImportAuthDomain', e.target.value)} placeholder="DOMAIN (optional)" className={inputClass} />
                                </div>
                            </div>
                        )}
                        {authType === 'Bearer' && (
                            <div>
                                <label className={labelClass}>Bearer Token</label>
                                <input type="password" value={data.fileImportAuthToken || ''} onChange={e => onChange('fileImportAuthToken', e.target.value)} placeholder="Bearer token" className={`${inputClass} font-mono`} />
                            </div>
                        )}
                        {authType === 'ApiKey' && (
                            <div>
                                <label className={labelClass}>API Key</label>
                                <input type="password" value={data.fileImportAuthToken || ''} onChange={e => onChange('fileImportAuthToken', e.target.value)} placeholder="API key" className={`${inputClass} font-mono`} />
                            </div>
                        )}
                        {authType === 'Windows' && (
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelClass}>Domain</label>
                                    <input type="text" value={data.fileImportAuthDomain || ''} onChange={e => onChange('fileImportAuthDomain', e.target.value)} placeholder="DOMAIN" className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Username</label>
                                    <input type="text" value={data.fileImportAuthUsername || ''} onChange={e => onChange('fileImportAuthUsername', e.target.value)} placeholder="username" className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input type="password" value={data.fileImportAuthPassword || ''} onChange={e => onChange('fileImportAuthPassword', e.target.value)} placeholder="password" className={inputClass} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* CSV Config (Collapsible) */}
            {(parserType === 'Csv' || parserType === 'Auto') && (
                <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                    <div className={sectionHeaderClass} onClick={() => setCsvExpanded(!csvExpanded)}>
                        {csvExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        <FileText size={14} className="text-slate-400" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">CSV Options</span>
                    </div>
                    {csvExpanded && (
                        <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelClass}>Delimiter</label>
                                    <input
                                        type="text"
                                        value={data.fileImportCsvDelimiter || ','}
                                        onChange={e => onChange('fileImportCsvDelimiter', e.target.value)}
                                        maxLength={1}
                                        className={`${inputClass} font-mono w-16`}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Encoding</label>
                                    <select
                                        value={data.fileImportCsvEncoding || 'UTF-8'}
                                        onChange={e => onChange('fileImportCsvEncoding', e.target.value)}
                                        className={inputClass}
                                    >
                                        <option value="UTF-8">UTF-8</option>
                                        <option value="UTF-16">UTF-16</option>
                                        <option value="ASCII">ASCII</option>
                                        <option value="ISO-8859-1">ISO-8859-1</option>
                                        <option value="Windows-1252">Windows-1252</option>
                                    </select>
                                </div>
                                <div className="flex flex-col justify-end gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={data.fileImportCsvHasHeaderRecord !== false}
                                            onChange={e => onChange('fileImportCsvHasHeaderRecord', e.target.checked)}
                                            className="rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                                        />
                                        <span className="text-xs text-slate-600 dark:text-slate-400">Has Header Row</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={data.fileImportCsvTrimFields === true}
                                            onChange={e => onChange('fileImportCsvTrimFields', e.target.checked)}
                                            className="rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                                        />
                                        <span className="text-xs text-slate-600 dark:text-slate-400">Trim Fields</span>
                                    </label>
                                </div>
                            </div>

                            {/* Column Names (when no header) */}
                            {data.fileImportCsvHasHeaderRecord === false && (
                                <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className={labelClass}>Column Names</label>
                                        <button
                                            onClick={handleAddColumn}
                                            className="text-xs flex items-center gap-1 text-teal-600 font-bold hover:bg-teal-50 dark:hover:bg-teal-900/20 px-2 py-1 rounded transition-colors"
                                        >
                                            <Plus size={12} /> Add
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {columnNames.map((col, i) => (
                                            <div key={i} className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1">
                                                <input
                                                    type="text"
                                                    value={col}
                                                    onChange={e => handleColumnChange(i, e.target.value)}
                                                    className="bg-transparent border-none text-xs font-mono text-slate-700 dark:text-slate-300 focus:ring-0 p-0 w-24"
                                                />
                                                <button onClick={() => handleRemoveColumn(i)} className="text-slate-400 hover:text-red-500">
                                                    <Trash2 size={10} />
                                                </button>
                                            </div>
                                        ))}
                                        {columnNames.length === 0 && (
                                            <p className="text-[10px] text-slate-400">No custom columns. Will use Column0, Column1, etc.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* JSON Config (Collapsible) */}
            {(parserType === 'Json' || parserType === 'Auto') && (
                <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                    <div className={sectionHeaderClass} onClick={() => setJsonExpanded(!jsonExpanded)}>
                        {jsonExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        <FileText size={14} className="text-slate-400" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">JSON Options</span>
                    </div>
                    {jsonExpanded && (
                        <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={data.fileImportJsonIsJsonLines === true}
                                    onChange={e => onChange('fileImportJsonIsJsonLines', e.target.checked)}
                                    className="rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                                />
                                <span className="text-xs text-slate-600 dark:text-slate-400">JSON Lines Format (JSONL)</span>
                            </label>
                            {!data.fileImportJsonIsJsonLines && (
                                <div>
                                    <label className={labelClass}>Array Path (JSONPath)</label>
                                    <input
                                        type="text"
                                        value={data.fileImportJsonArrayPath || ''}
                                        onChange={e => onChange('fileImportJsonArrayPath', e.target.value)}
                                        placeholder="$.records or $.data.items"
                                        className={`${inputClass} font-mono`}
                                    />
                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">Leave empty if root is an array.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* XML Config (Collapsible) */}
            {(parserType === 'Xml' || parserType === 'Auto') && (
                <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg">
                    <div className={sectionHeaderClass} onClick={() => setXmlExpanded(!xmlExpanded)}>
                        {xmlExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        <FileText size={14} className="text-slate-400" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">XML Options</span>
                    </div>
                    {xmlExpanded && (
                        <div className="mt-4 space-y-3 animate-in fade-in duration-200">
                            <div>
                                <label className={labelClass}>Record XPath</label>
                                <input
                                    type="text"
                                    value={data.fileImportXmlRecordXPath || '/*/*'}
                                    onChange={e => onChange('fileImportXmlRecordXPath', e.target.value)}
                                    placeholder="//record or /root/items/item"
                                    className={`${inputClass} font-mono`}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Process Configuration */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Settings size={14} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Process Configuration</span>
                </div>
                <div className="space-y-4">
                    {/* Row 1: File → Process → Start Event */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className={labelClass}>Workflow File</label>
                            <div className="relative">
                                <select
                                    value={selectedFileId}
                                    onChange={e => handleFileChange(e.target.value)}
                                    className={inputClass}
                                >
                                    <option value="">— Select file —</option>
                                    {workflows.map(workflow => (
                                        <option key={workflow.id} value={workflow.id}>
                                            {workflow.name || workflow.id}{workflow.version ? ` (v${workflow.version})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Process</label>
                            <div className="relative">
                                <select
                                    value={selectedProcessName}
                                    onChange={e => handleProcessChange(e.target.value)}
                                    className={inputClass}
                                    disabled={!selectedFileId}
                                >
                                    {!selectedFileId ? (
                                        <option value="">Select a file first</option>
                                    ) : processes.length === 0 ? (
                                        <option value="">No processes found</option>
                                    ) : (
                                        <>
                                            <option value="">— Select process —</option>
                                            {processes.map(proc => (
                                                <option key={`${proc.collection}:${proc.processName}`} value={proc.processName}>
                                                    {proc.processName}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Start Event</label>
                            <select
                                value={data.fileImportStartEventId || ''}
                                onChange={e => onChange('fileImportStartEventId', e.target.value)}
                                className={inputClass}
                                disabled={!selectedProcess}
                            >
                                {!selectedProcess ? (
                                    <option value="">Select a process first</option>
                                ) : selectedProcess.startEvents.length === 0 ? (
                                    <option value="">No start events found</option>
                                ) : (
                                    <>
                                        <option value="">— Select start event —</option>
                                        {selectedProcess.startEvents.map(evt => (
                                            <option key={evt.id} value={evt.id}>
                                                {evt.name}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                    </div>
                    {/* Row 2: Version + Variable Prefix */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Process Version</label>
                            <input
                                type="text"
                                value={data.fileImportProcessVersion ?? ''}
                                onChange={e => onChange('fileImportProcessVersion', e.target.value)}
                                placeholder="(latest)"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Variable Prefix</label>
                            <input
                                type="text"
                                value={data.fileImportVariablePrefix || ''}
                                onChange={e => onChange('fileImportVariablePrefix', e.target.value)}
                                placeholder="record, employee, customer..."
                                className={inputClass}
                            />
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                                Creates variables like prefix.fieldName
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Archive */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Archive size={14} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Archive</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Archive Location</label>
                        <input
                            type="text"
                            value={data.fileImportArchiveUri || ''}
                            onChange={e => onChange('fileImportArchiveUri', e.target.value)}
                            placeholder="file://C:/archive/processed"
                            className={`${inputClass} font-mono`}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Naming Policy</label>
                        <select
                            value={data.fileImportArchiveNaming || 'Timestamp'}
                            onChange={e => onChange('fileImportArchiveNaming', e.target.value)}
                            className={inputClass}
                        >
                            <option value="Timestamp">Timestamp (filename_20260225.csv)</option>
                            <option value="Guid">GUID (filename_a1b2c3.csv)</option>
                            <option value="Original">Original (overwrite)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Error Handling */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle size={14} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Error Handling</span>
                </div>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={data.fileImportContinueOnError !== false}
                            onChange={e => onChange('fileImportContinueOnError', e.target.checked)}
                            className="rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400">Continue on Error</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Max Errors</label>
                        <input
                            type="number"
                            value={data.fileImportMaxErrors || 100}
                            onChange={e => onChange('fileImportMaxErrors', parseInt(e.target.value) || 100)}
                            min={0}
                            max={10000}
                            className="w-24 border border-slate-300 dark:border-slate-700 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
